import { Injectable, OnDestroy } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

/**
 * Single source of truth for paginator i18n — provided app-wide in app.config.ts.
 * All labels come from the shared i18n JSON files (paginator.*, common.*) so they
 * never drift out of sync with the rest of the translated UI.
 */
@Injectable()
export class TranslatePaginatorIntl extends MatPaginatorIntl implements OnDestroy {
  private destroyed$ = new Subject<void>();

  constructor(private translate: TranslateService) {
    super();
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.updateLabels());
    this.updateLabels();
  }

  private updateLabels() {
    this.itemsPerPageLabel = this.translate.instant('common.itemsPerPage');
    this.nextPageLabel = this.translate.instant('paginator.next');
    this.previousPageLabel = this.translate.instant('paginator.previous');
    this.firstPageLabel = this.translate.instant('paginator.first');
    this.lastPageLabel = this.translate.instant('paginator.last');
    this.changes.next();
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const pageLabel = this.translate.instant('common.page');
    const ofLabel = this.translate.instant('common.of');

    if (length === 0 || pageSize === 0) {
      return `${pageLabel} 1 ${ofLabel} 1`;
    }

    const totalPages = Math.ceil(length / pageSize);
    return `${pageLabel} ${page + 1} ${ofLabel} ${totalPages}`;
  };

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
